import { useQuery } from '@apollo/client';
import type { Op } from 'quill/core';
import React, { useLayoutEffect, useMemo } from 'react';
import HomeActivities from './HomeActivities';
import * as styles from './HomeView.module.scss';
import OrganizationSummary from './organizationSummary/OrganizationSummary';
import useViewedPostsColumn from './useViewedPostsColumn';
import PageLayout from '../app/layout/PageLayout';
import RightPanel, { useRightPanel } from '../app/layout/RightPanel';
import TwoColumnRow from '../app/layout/TwoColumnRow';
import { useAppDispatch } from '../app/redux/useAppSelector';
import { gql } from '../graphql/generated/gql';
import PostSectionHeader from '../post/PostSectionHeader';
import { UIActions } from '../ui/uiSlice';
import ReferralModal from '../user/ReferralModal';
import { openPricingModal } from '../utils/url/pricingPath';

const ORGANIZATION_SUMMARY_QUERY = gql(/** @complexity 2 */ `
  query organizationSummary {
    session: currentSession {
      organization {
        id
        summary
      }
    }
  }
`);

type HomeModal = 'pricing' | 'referral';

interface HomeViewProps {
  openModal?: HomeModal;
}

const useModal = (modal?: HomeModal) => {
  const dispatch = useAppDispatch();

  useLayoutEffect(() => {
    if (modal === 'pricing') {
      openPricingModal(dispatch);
    } else if (modal === 'referral') {
      dispatch(UIActions.openModal(ReferralModal));
    } else {
      return undefined;
    }

    return () => {
      dispatch(UIActions.closeModal());
    };
  }, [dispatch, modal]);
};

const HomeView = ({ openModal }: HomeViewProps) => {
  const { data, loading } = useQuery(ORGANIZATION_SUMMARY_QUERY);
  const organization = data?.session?.organization;
  const summary = useMemo(() => {
    if (organization?.summary) {
      return JSON.parse(organization.summary) as Op[];
    }
    return null;
  }, [organization]);
  const main = useViewedPostsColumn();
  useModal(openModal);

  const { openRightPanel } = useRightPanel();

  return (
    <PageLayout.Content noPaddingX noPaddingY>
      <TwoColumnRow
        main={(isAsideShown) => (
          <div className={styles.main}>
            <OrganizationSummary
              summary={summary}
              onActivityOpen={
                isAsideShown
                  ? undefined
                  : () => {
                      openRightPanel(
                        <RightPanel.Inner header="Activity">
                          <div className={styles.activities}>
                            <HomeActivities />
                          </div>
                        </RightPanel.Inner>,
                      );
                    }
              }
            />
            {loading && !summary ? undefined : (
              <>
                <PostSectionHeader fullPageWidth={false} controls={null}>
                  <h3>Viewed</h3>
                </PostSectionHeader>
                {main}
              </>
            )}
          </div>
        )}
        aside={
          <div className={styles.activities}>
            {/*
            <h4 className={styles.asideHeader}>New in Slab</h4>
            <ProductUpdateCard
              banner="https://placehold.co/1200x400/orange/white"
              content={new Delta().insert(
                'New in Slab: Sidebar sections, public draft editing, native PDF\nContent goes here\n',
              )}
              newTabTo="https://example.com/optional"
              publishedAt={new Date().toISOString()}
              author={{
                id: 'user-1',
                name: 'Jason Chen',
                title: 'Founder & CEO at Slab',
              }}
            />
            */}
            <h4 className={styles.asideHeader}>Activity</h4>
            <HomeActivities />
          </div>
        }
      />
    </PageLayout.Content>
  );
};

export default HomeView;
